/**
 * 모바일 웹/PWA 앱 컬럼 높이 판정 — useSoftKeyboardViewportFix가 #root에 무엇을 할지 고른다.
 *
 * ⚠ 이 파일에는 import를 넣지 말 것. scripts/test-viewport-fit.js가 typescript.transpileModule로
 * 통째로 평가해 판정표를 대조한다.
 *
 * 판정 순서가 곧 정책이다:
 * 1. 핀치줌 중이면 'skip' — vv.height 감소가 키보드가 아니라 줌 배율 때문이다. 상태를 그대로 둔다
 * 2. layout viewport(clientHeight)보다 visual viewport가 100px 넘게 작으면 'keyboard' —
 *    iOS 키보드(layout viewport가 줄지 않는다). URL바 개폐(~60px)·회전 과도기와 가르는 임계값이다.
 *    resizes-content가 적용된 Android Chrome은 키보드에 clientHeight도 함께 줄어 여기 걸리지 않는다
 * 3. 앱 컬럼(body = CSS 100dvh)이 position:fixed 기준 상자보다 길면 'clamp' — 2026-11-05 안드로이드
 *    설치 앱에서 처음 불러올 때 100dvh가 보이는 창보다 크게 잡혀 컬럼 맨 아래의 하단 탭바가 화면 밖으로
 *    밀려났다(같은 순간 fixed인 '새 대화' FAB는 정확히 보이는 하단 기준에 놓였다 — 스크린샷 실측).
 *    줄이기만 한다: body가 fixed 상자보다 짧은 경우는 이 버그가 아니고, 늘리면 다른 브라우저의 정상
 *    배치를 건드린다. 1px 이하 차이는 반올림 오차로 본다
 * 4. 그 밖은 'none' — #root 인라인 스타일을 모두 해제한다
 */

/** URL바 개폐(~60px)·회전 과도기·소수점 오차와 키보드(가로모드 최소 ~150px)를 가르는 임계값 */
export const KEYBOARD_MIN_PX = 100;
/** 앱 컬럼이 fixed 기준 상자보다 이만큼 넘게 길어야 줄인다(반올림 오차 무시) */
export const CLAMP_MIN_PX = 1;

export type ViewportFitMode = 'skip' | 'keyboard' | 'clamp' | 'none';

export interface ViewportFitInput {
  /** document.documentElement.clientHeight — layout viewport(ICB) 높이 */
  clientHeight: number;
  /** visualViewport.height */
  vvHeight: number;
  /** visualViewport.scale */
  scale: number;
  /** body의 실제 높이(CSS 100dvh 결과) */
  bodyHeight: number;
  /** position:fixed; top:0; bottom:0 측정 요소의 높이 — fixed 기준 상자 */
  fixedHeight: number;
}

export function decideViewportFit(input: ViewportFitInput): ViewportFitMode {
  const { clientHeight, vvHeight, scale, bodyHeight, fixedHeight } = input;
  if (Math.abs((scale ?? 1) - 1) > 0.01) return 'skip';
  if (clientHeight - vvHeight > KEYBOARD_MIN_PX) return 'keyboard';
  if (fixedHeight > 0 && bodyHeight - fixedHeight > CLAMP_MIN_PX) return 'clamp';
  return 'none';
}
